import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Button, Input } from "reactstrap";
import Table from "../../../components/Common/Table";
import { useHistory } from "react-router-dom";
import Swal from "sweetalert2";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import ProcesarInformesMasivos from './ProcesarInformesMasivos';
import CorrespondenciaModal from './CorrespondenciaModal';
import { getAgent, postAsignarCasosMasivos, getCasosFilter, postCasoUpdateContacto, postReabrirCasos } from "../../../helpers/backend";

const ListaCasos = ({ params, casosData, infiniteLoading, hasMore, lastElementRef, onDataModified }) => {
  const history = useHistory();
  const user = JSON.parse(localStorage.getItem("user"));
  const theme = useSelector((state) => state.theme.theme) || "light";
  const [selectedCasos, setSelectedCasos] = useState([]);
  const [agents, setAgents] = useState([]);
  const [expandedDescriptions, setExpandedDescriptions] = useState(new Set());
  const [isSelectingAllPages, setIsSelectingAllPages] = useState(false);
  const [allPagesCasosIds, setAllPagesCasosIds] = useState([]);
  const [isMassModalOpen, setIsMassModalOpen] = useState(false);

  // Estados para modal de correspondencia
  const [showCorrespondenciaModal, setShowCorrespondenciaModal] = useState(false);
  const [selectedCorrespondencia, setSelectedCorrespondencia] = useState(null);

  // Estados para el ordenamiento personalizado
  const [customOrder, setCustomOrder] = useState([]);
  const [isCustomOrderEnabled, setIsCustomOrderEnabled] = useState(false);
  const [isDragMode, setIsDragMode] = useState(false);

  // Refresco seguro de datos manteniendo filtros
  const safeRefresh = async () => {
    if (typeof onDataModified === 'function') {
      await onDataModified();
    } else {
      window.dispatchEvent(new Event('casos:dataModified'));
    }
  };

  const canReopenCase = () => {
    const userRole = user?.roles;
    return userRole === "Administrador" || userRole === "SuperAdmin";
  };

  const handleReabrirCaso = async (casoId, e) => {
    e?.stopPropagation();
    const result = await Swal.fire({
      title: "Reabrir OT",
      text: "¿Desea reabrir este caso?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sí, reabrir",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#6c757d",
    });
    if (result.isConfirmed) {
      try {
        await postReabrirCasos(casoId);
        Swal.fire({
          title: "Éxito",
          text: "Caso reabierto correctamente",
          icon: "success",
          timer: 2000,
          showConfirmButton: false,
        });
        // Refrescar datos manteniendo filtros
        await safeRefresh();
      } catch (error) {
        console.error("Error al reabrir el caso:", error);
        Swal.fire({
          title: "Error",
          text: "No se pudo reabrir el caso",
          icon: "error",
        });
      }
    }
  };

  // Función para verificar si el usuario puede asignar casos (roles administrativos)
  const canAssignCases = () => {
    const userRole = user?.roles;
    return userRole === "Administrador" || userRole === "SuperAdmin" || userRole === "Supervisor";
  };

  // Función para verificar si el usuario puede transferir casos (rol Agente)
  const canTransferCases = () => {
    const userRole = user?.roles;
    return userRole === "Agente";
  };

  // Botón de generación DF solo para SuperAdmin
  const canGenerateDF = () => {
    const userRole = user?.roles;
    return userRole === "SuperAdmin";
  };


  // Cargar agentes al montar el componente
  useEffect(() => {
    const loadAgents = async () => {
      try {
        const response = await getAgent();
        setAgents(response.data || []);
        console.log(response);

      } catch (error) {
        console.error("Error loading agents:", error);
      }
    };
    loadAgents();
  }, []);

  // Cargar orden personalizado desde localStorage
  useEffect(() => {
    const savedOrder = localStorage.getItem('casosCustomOrder');
    const savedOrderEnabled = localStorage.getItem('casosCustomOrderEnabled');

    if (savedOrder) {
      try {
        setCustomOrder(JSON.parse(savedOrder));
      } catch (error) {
        console.error('Error parsing saved custom order:', error);
      }
    }

    if (savedOrderEnabled === 'true') {
      setIsCustomOrderEnabled(true);
    }
  }, []);

  // Guardar orden personalizado en localStorage
  const saveCustomOrder = (order) => {
    localStorage.setItem('casosCustomOrder', JSON.stringify(order));
    setCustomOrder(order);
  };

  // Alternar modo de ordenamiento personalizado
  const toggleCustomOrder = () => {
    const newEnabled = !isCustomOrderEnabled;
    setIsCustomOrderEnabled(newEnabled);
    localStorage.setItem('casosCustomOrderEnabled', newEnabled.toString());

    if (newEnabled && customOrder.length === 0) {
      // Si se activa por primera vez, crear orden inicial basado en los datos actuales
      const initialOrder = params.results.map(item => item.id);
      saveCustomOrder(initialOrder);
    }
  };

  // Función para manejar el drag and drop
  const handleOnDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(getOrderedData());
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    const newOrder = items.map(item => item.id);
    saveCustomOrder(newOrder);
  };

  // Función para obtener datos ordenados según el orden personalizado
  const getOrderedData = () => {
    if (!isCustomOrderEnabled || customOrder.length === 0) {
      return params.results;
    }

    // Crear un mapa para acceso rápido a los datos
    const dataMap = new Map(params.results.map(item => [item.id, item]));

    // Ordenar según customOrder, agregando al final los elementos que no estén en el orden
    const orderedData = [];
    const usedIds = new Set();

    // Primero agregar los elementos en el orden personalizado
    customOrder.forEach(id => {
      if (dataMap.has(id)) {
        orderedData.push(dataMap.get(id));
        usedIds.add(id);
      }
    });

    // Luego agregar los elementos que no estén en el orden personalizado
    params.results.forEach(item => {
      if (!usedIds.has(item.id)) {
        orderedData.push(item);
      }
    });

    return orderedData;
  };

  const columnsBase = [
    {
      key: "select",
      label: (
        <Input
          type="checkbox"
          checked={isSelectingAllPages || (selectedCasos.length === params.results.length && params.results.length > 0)}
          onChange={handleSelectAll}
          style={{ margin: 0 }}
          title={isSelectingAllPages ? `Seleccionados ${selectedCasos.length} casos de todas las páginas` : 'Seleccionar todos los casos de esta página'}
        />
      ),
      filterable: false
    },
    { key: "name", label: "OT N°", filterable: true },
    { key: "direccion", label: "Dirección", filterable: true },
    { key: "cliente", label: "Cliente", filterable: true },
    { key: "tipo_caso", label: "Tipo de OT", filterable: true },
    { key: "numero_reclamo", label: "Número de Reclamo", filterable: true },
    { key: "tipo_reclamo", label: "Tipo de Reclamo", filterable: true },
    { key: "codigoCAU", label: "Código CAU", filterable: true },
    { key: "cuenta_contrato_ciudad_limpia", label: "Cuenta Ciudad Limpia", filterable: true },
    { key: "cuenta_enel", label: "Cuenta Enel", filterable: true },
    { key: "descripcion_tipoproductor", label: "Tipo Productor", filterable: true },
    { key: "fecha_pqr", label: "Fecha de Radicacion", filterable: true },
    { key: "contacto", label: "Agente(s)", filterable: true },
    { key: "estado", label: "Estado", filterable: true },
    { key: "descripcion", label: "Descripción", filterable: true },
    { key: "fecha_finalizacion", label: "Finalización", filterable: true },
    { key: "vencimiento", label: "Vencimiento", filterable: true },
    { key: "planilla", label: "Planilla", filterable: true },
  ];

  const columns = canReopenCase()
    ? [...columnsBase, { key: "acciones", label: "Acciones", filterable: false }]
    : columnsBase;

  // Función para manejar selección individual
  const handleSelectCaso = (casoId) => {
    setSelectedCasos(prev => {
      if (prev.includes(casoId)) {
        const newSelection = prev.filter(id => id !== casoId);
        // Si se deselecciona un caso y estábamos en modo "todas las páginas", salir de ese modo
        if (isSelectingAllPages) {
          setIsSelectingAllPages(false);
          setAllPagesCasosIds([]);
        }
        return newSelection;
      } else {
        return [...prev, casoId];
      }
    });
  };

  // Helper para calcular vencimiento
  const getVencimientoInfo = (fecha, estado) => {
    if (!fecha || estado === 'FINALIZADO') return null;

    // Asumimos que la fecha viene en formato YYYY-MM-DD
    // Creamos fechas en UTC para evitar problemas de zona horaria al comparar solo fechas
    const d = new Date(fecha).toISOString().slice(0, 10);
    const today = new Date().toISOString().slice(0, 10); // Fecha actual local convertida a string comparativo

    // Comparación simple de cadenas YYYY-MM-DD
    if (d < today) return { label: 'Vencido', color: 'danger', icon: 'fas fa-exclamation-triangle' };

    // Calcular diferencia en días para "Próximo"
    const dateVenc = new Date(fecha);
    const dateNow = new Date();
    dateNow.setHours(0, 0, 0, 0);
    // Ajustar fecha vencimiento para ser comparable (medianoche)
    // Nota: new Date('YYYY-MM-DD') es UTC, new Date() es local.
    // Mejor aproximación:
    const diffTime = new Date(d) - new Date(today);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays >= 0 && diffDays <= 2) return { label: 'Próximo a vencer', color: 'warning', icon: 'fas fa-clock' };

    return null;
  };

  // Estado para controlar el loading durante la selección de todas las páginas
  const [isLoadingAllPages, setIsLoadingAllPages] = useState(false);

  // Función para obtener todos los IDs de casos con filtros aplicados
  const getAllCasosIds = async () => {
    try {
      setIsLoadingAllPages(true);

      // Construir parámetros desde filtros persistidos
      const buildStoredFilterParams = () => {
        try {
          const raw = localStorage.getItem('casosFilters');
          if (!raw) return '';
          const saved = JSON.parse(raw);
          const params = new URLSearchParams();
          if (saved.search) params.set('search', saved.search);
          if (saved.codigoCau && saved.codigoCau.length > 0) params.set('codigo_cau', saved.codigoCau.join(','));
          const stateParam = saved && saved.botonActivo && saved.botonActivo !== 'Todas' && saved.botonActivo !== 'Filtrado'
            ? saved.botonActivo
            : saved.estado;
          if (stateParam) params.set('estado', stateParam);
          if (saved.tipoCaso) params.set('tipo_caso', saved.tipoCaso);
          if (saved.tipoOrden) params.set('tipo_orden', saved.tipoOrden);
          if (saved.tipoProductor) params.set('descripcion_tipoproductor', saved.tipoProductor);
          if (saved.agentes && saved.agentes.length > 0) params.set('agentes', saved.agentes.join(','));
          if (saved.fechaInicio) params.set('fecha_inicio', saved.fechaInicio);
          if (saved.fechaFin) params.set('fecha_fin', saved.fechaFin);
          return params.toString();
        } catch (e) {
          return '';
        }
      };
      const storedParams = buildStoredFilterParams();
      let allCasos = [];
      let offset = 0;
      const limit = 30; // Mismo límite que usa el componente de paginación
      let hasMorePages = true;

      // Obtener todos los parámetros actuales desde filtros persistidos
      const urlParams = new URLSearchParams(storedParams);

      // Remover solo offset y limit para evitar conflictos, manteniendo todos los demás filtros
      urlParams.delete('offset');
      urlParams.delete('limit');

      while (hasMorePages) {
        // Crear nuevos parámetros para esta página específica manteniendo todos los filtros
        const pageParams = new URLSearchParams(urlParams);
        pageParams.set('limit', limit.toString());
        pageParams.set('offset', offset.toString());

        const pageUrl = `?${pageParams.toString()}`;

        console.log(`Obteniendo página con offset ${offset}, URL completa: ${pageUrl}`);

        // Usar siempre getCasosFilter ya que maneja todos los filtros correctamente
        const response = await getCasosFilter(pageUrl);

        console.log(`Offset ${offset} - Casos obtenidos:`, response.data?.results?.length || 0);
        console.log(`Offset ${offset} - Hay siguiente página:`, response.data?.next !== null);

        if (response.data && response.data.results && response.data.results.length > 0) {
          // Extraer solo los IDs de los casos
          const casosIds = response.data.results.map(caso => caso.id);
          allCasos = [...allCasos, ...casosIds];

          // Verificar si hay más páginas
          hasMorePages = response.data.next !== null;
          offset += limit; // Incrementar offset por el límite
        } else {
          hasMorePages = false;
        }
      }

      console.log(`Total de casos obtenidos con filtros aplicados: ${allCasos.length}`);
      return allCasos;
    } catch (error) {
      console.error('Error al obtener todos los IDs de casos:', error);
      return [];
    } finally {
      setIsLoadingAllPages(false);
    }
  };

  // Función para seleccionar/deseleccionar todos
  async function handleSelectAll() {
    if (isSelectingAllPages) {
      // Si ya se están seleccionando todas las páginas, deseleccionar todo
      setSelectedCasos([]);
      setIsSelectingAllPages(false);
      setAllPagesCasosIds([]);
    } else if (selectedCasos.length === 0) {
      // Si no hay casos seleccionados, seleccionar todos de la página actual y preguntar por todas las páginas
      const currentPageIds = params.results.map(caso => caso.id);
      setSelectedCasos(currentPageIds);

      // Preguntar si quiere seleccionar todas las páginas
      const result = await Swal.fire({
        title: '¿Seleccionar todos los casos?',
        text: `Se han seleccionado ${currentPageIds.length} casos de esta página. ¿Quieres seleccionar TODOS los casos que coincidan con los filtros aplicados (todas las páginas)?`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Sí, seleccionar todos',
        cancelButtonText: 'No, solo esta página',
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#6c757d'
      });

      if (result.isConfirmed) {
        // Mostrar loading mientras se obtienen todos los IDs
        Swal.fire({
          title: 'Cargando casos...',
          text: 'Obteniendo todos los casos que coinciden con los filtros aplicados. Por favor espera.',
          icon: 'info',
          allowOutsideClick: false,
          allowEscapeKey: false,
          showConfirmButton: false,
          didOpen: () => {
            Swal.showLoading();
          }
        });

        try {
          // Obtener todos los IDs de casos con filtros aplicados
          const allIds = await getAllCasosIds();

          if (allIds.length > 0) {
            setAllPagesCasosIds(allIds);
            setSelectedCasos(allIds);
            setIsSelectingAllPages(true);

            Swal.fire({
              title: 'Casos seleccionados',
              text: `Se han seleccionado ${allIds.length} casos de todas las páginas con los filtros aplicados.`,
              icon: 'success',
              timer: 3000,
              showConfirmButton: false
            });
          } else {
            Swal.fire({
              title: 'Sin resultados',
              text: 'No se encontraron casos que coincidan con los filtros aplicados.',
              icon: 'warning',
              timer: 2000,
              showConfirmButton: false
            });
          }
        } catch (error) {
          console.error('Error al obtener todos los casos:', error);
          Swal.fire({
            title: 'Error',
            text: 'Ocurrió un error al obtener todos los casos. Por favor intenta nuevamente.',
            icon: 'error',
            confirmButtonText: 'Entendido'
          });
        }
      }
      // Si dice "No", mantiene la selección de la página actual que ya se estableció
    } else {
      // Si hay casos seleccionados, deseleccionar todos
      setSelectedCasos([]);
      setIsSelectingAllPages(false);
      setAllPagesCasosIds([]);
    }
  }

  // Función para alternar la expansión de descripción
  const toggleDescription = (casoId) => {
    setExpandedDescriptions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(casoId)) {
        newSet.delete(casoId);
      } else {
        newSet.add(casoId);
      }
      return newSet;
    });
  };

  // Función para truncar texto
  const truncateText = (text, maxLength = 50) => {
    if (!text) return "Sin descripción";
    return text.length > maxLength ? text.substring(0, maxLength) + "..." : text;
  };

  // Función para transferir casos (para agentes)
  const handleTransferirCasos = async () => {
    if (selectedCasos.length === 0) {
      Swal.fire({
        title: "Error",
        text: "Debe seleccionar al menos un caso",
        icon: "error"
      });
      return;
    }

    // Crear opciones para el select de todos los agentes
    const agentOptions = agents.reduce((acc, agent) => {
      acc[agent.id] = agent.full_name || `${agent.first_name} ${agent.last_name}`;
      return acc;
    }, {});

    // Verificar si hay agentes disponibles
    if (agents.length === 0) {
      Swal.fire({
        title: "Sin agentes",
        text: "No hay agentes disponibles para transferir los casos",
        icon: "warning"
      });
      return;
    }

    const { value: selectedAgentId } = await Swal.fire({
      title: "Transferir OT",
      text: `¿A qué agente desea transferir las ${selectedCasos.length} OT seleccionadas?`,
      input: "select",
      inputOptions: agentOptions,
      inputPlaceholder: "Seleccione un agente",
      showCancelButton: true,
      confirmButtonText: "Transferir",
      cancelButtonText: "Cancelar",
      inputValidator: (value) => {
        if (!value) {
          return "Debe seleccionar un agente";
        }
      }
    });

    if (selectedAgentId) {
      try {
        // Enviar cada caso individualmente usando el nuevo endpoint
        for (const casoId of selectedCasos) {
          const data = {
            contacto: [parseInt(selectedAgentId)]
          };
          await postCasoUpdateContacto(casoId, data);
        }

        Swal.fire({
          title: "Éxito",
          text: "Casos transferidos correctamente",
          icon: "success"
        });

        // Limpiar selección
        setSelectedCasos([]);
        setIsSelectingAllPages(false);
        setAllPagesCasosIds([]);

        // Refrescar datos manteniendo filtros
        await safeRefresh();
      } catch (error) {
        console.error("Error transferring cases:", error);
        Swal.fire({
          title: "Error",
          text: "Error al transferir los casos",
          icon: "error"
        });
      }
    }
  };

  // Función para asignar casos masivamente
  const handleAsignarCasos = async () => {
    if (selectedCasos.length === 0) {
      Swal.fire({
        title: "Error",
        text: "Debe seleccionar al menos un caso",
        icon: "error"
      });
      return;
    }

    // Crear opciones para el select de agentes
    const agentOptions = agents.reduce((acc, agent) => {
      acc[agent.id] = agent.full_name || `${agent.first_name} ${agent.last_name}`;
      return acc;
    }, {});


    const { value: selectedAgentId } = await Swal.fire({
      title: "Asignar OT",
      text: `¿A qué agente desea asignar las ${selectedCasos.length} OT seleccionadas?`,
      input: "select",
      inputOptions: agentOptions,
      inputPlaceholder: "Seleccione un agente",
      showCancelButton: true,
      confirmButtonText: "Asignar",
      cancelButtonText: "Cancelar",
      inputValidator: (value) => {
        if (!value) {
          return "Debe seleccionar un agente";
        }
      }
    });

    if (selectedAgentId) {
      try {
        await postAsignarCasosMasivos({
          casos_ids: selectedCasos,
          contacto_id: parseInt(selectedAgentId)
        });

        Swal.fire({
          title: "Éxito",
          text: "Casos asignados correctamente",
          icon: "success"
        });

        // Limpiar selección
        setSelectedCasos([]);
        setIsSelectingAllPages(false);
        setAllPagesCasosIds([]);

        // Refrescar datos manteniendo filtros
        await safeRefresh();
      } catch (error) {
        console.error("Error assigning cases:", error);
        Swal.fire({
          title: "Error",
          text: "Error al asignar los casos",
          icon: "error"
        });
      }
    }
  };

  const onRowClick = (row) => {
    if (row.tipo_orden === 'CORRESPONDENCIA') {
      setSelectedCorrespondencia(row);
      setShowCorrespondenciaModal(true);
    } else {
      history.push(`/casos/view/${row.id}`);
    }
  };

  const orderedResults = getOrderedData();
  const formattedData = orderedResults.map((item) => ({
    ...item,
    // Checkbox de selección
    select: (
      <Input
        type="checkbox"
        checked={selectedCasos.includes(item.id)}
        onChange={() => handleSelectCaso(item.id)}
        onClick={(e) => e.stopPropagation()} // Evitar que se active onRowClick
        style={{ margin: 0 }}
      />
    ),
    descripcion: (
      <div style={{ display: 'flex', flexDirection: 'column', minWidth: '150px' }}>
        <span style={{ fontSize: '0.8rem', lineHeight: '1.2' }}>
          {expandedDescriptions.has(item.id)
            ? (item.descripcion || "Sin descripción")
            : truncateText(item.descripcion, 40)
          }
        </span>
        {item.descripcion && item.descripcion.length > 40 && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              toggleDescription(item.id);
            }}
            style={{
              background: 'none',
              border: 'none',
              color: '#3b82f6',
              cursor: 'pointer',
              fontSize: '0.7rem',
              fontWeight: '600',
              padding: 0,
              width: 'fit-content',
              marginTop: '2px'
            }}
          >
            {expandedDescriptions.has(item.id) ? 'Ver menos' : '... Ver más'}
          </button>
        )}
      </div>
    ),
    // Formateo del contacto (agente)
    contacto:
      item.contacto && Array.isArray(item.contacto) && item.contacto.length > 0
        ? item.contacto[0].full_name
        : "N/A",
    // Formateo del cliente - usando la misma lógica que en las tarjetas
    cliente:
      item.cliente && item.cliente.length > 0 ? (
        Array.isArray(item.cliente) ?
          item.cliente[0].get_full_name || item.cliente[0].full_name || "Sin nombre"
          : "Sin cliente"
      ) : (
        "Sin cliente asignado"
      ),
    // Formateo de la dirección del cliente
    direccion:
      item.tipo_orden === 'CORRESPONDENCIA' && item.metadata?.correspondencia?.direccion_reclamante
        ? item.metadata.correspondencia.direccion_reclamante
        : (item.cliente && item.cliente.length > 0 &&
          item.cliente[0].address && Array.isArray(item.cliente[0].address) &&
          item.cliente[0].address.length > 0 ?
          item.cliente[0].address[0].address || "Sin dirección"
          : "Sin dirección"),
    // Formateo de los nuevos campos
    tipo_caso: item.tipo_caso || "Sin tipo",
    numero_reclamo: item.numero_reclamo || "Sin número",
    tipo_reclamo: item.tipo_reclamo || "Sin tipo",
    // Nuevos campos solicitados del cliente
    codigoCAU: (
      <span style={{ fontWeight: '500', color: '#007bff' }}>
        {item.cliente && item.cliente.length > 0 && item.cliente[0].codigoCAU ?
          item.cliente[0].codigoCAU : "Sin código CAU"}
      </span>
    ),
    cuenta_contrato_ciudad_limpia: (
      <span style={{ fontWeight: '500', color: '#198754' }}>
        {item.cliente && item.cliente.length > 0 && item.cliente[0].cuenta_contrato_ciudad_limpia ?
          item.cliente[0].cuenta_contrato_ciudad_limpia : "Sin cuenta CL"}
      </span>
    ),
    cuenta_enel: (
      <span style={{ fontWeight: '500', color: '#fd7e14' }}>
        {item.cliente && item.cliente.length > 0 && item.cliente[0].cuenta_enel ?
          item.cliente[0].cuenta_enel : "Sin cuenta Enel"}
      </span>
    ),
    descripcion_tipoproductor: (
      <span style={{ fontWeight: '500', color: '#d63384' }}>
        {item.cliente && item.cliente.length > 0 && item.cliente[0].descripcion_tipoproductor ?
          item.cliente[0].descripcion_tipoproductor : "Sin tipo productor"}
      </span>
    ),
    planilla: (
      <span>
        {item.tipo_orden === 'CORRESPONDENCIA' && item.metadata?.correspondencia?.planilla
          ? item.metadata.correspondencia.planilla
          : "N/A"}
      </span>
    ),
    vencimiento: (() => {
      const info = getVencimientoInfo(item.fecha_vencimiento, item.estado);
      if (!info) return item.fecha_vencimiento || "-";
      return (
        <div className={`badge bg-${info.color} text-white`} style={{ fontSize: '0.85em', display: 'flex', alignItems: 'center', gap: '5px' }}>
          <i className={info.icon}></i>
          {info.label} ({item.fecha_vencimiento})
        </div>
      );
    })(),
    // Acciones por fila
    acciones: (
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        {canReopenCase() && item.estado === 'FINALIZADO' ? (
          <Button
            color="none"
            size="sm"
            onClick={(e) => handleReabrirCaso(item.id, e)}
            style={{
              width: '32px',
              height: '32px',
              padding: '0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              backgroundColor: 'rgba(245, 158, 11, 0.15)',
              color: '#d97706',
              border: 'none',
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
            className="btn-reopen-modern"
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#d97706';
              e.currentTarget.style.color = '#fff';
              e.currentTarget.style.transform = 'rotate(-45deg)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(245, 158, 11, 0.15)';
              e.currentTarget.style.color = '#d97706';
              e.currentTarget.style.transform = 'rotate(0deg)';
            }}
            title="Reabrir Caso"
          >
            <i className="fas fa-undo"></i>
          </Button>
        ) : (
          <div style={{ width: '32px', height: '32px' }}></div>
        )}
      </div>
    ),
  }));



  return (
    <div style={{ padding: "0 1rem" }}>
      {/* Controles de ordenamiento personalizado */}
      <div className="mb-1 mt-1 d-flex justify-content-between align-items-center">
        <div className="d-flex gap-1 align-items-center">
          <button
            className={`btn btn-sm ${isCustomOrderEnabled ? "btn-success" : "btn-light border text-muted"}`}
            onClick={toggleCustomOrder}
            style={{ fontSize: '11px', padding: '2px 8px', height: '26px', display: 'flex', alignItems: 'center', transition: 'all 0.2s' }}
            title={isCustomOrderEnabled ? "Desactivar orden manual" : "Activar orden manual"}
          >
            {isCustomOrderEnabled ? (
              <><i className="fas fa-check-circle me-1"></i>Orden Manual Activado</>
            ) : (
              <><i className="fas fa-sort me-1"></i>Activar Orden Manual</>
            )}
          </button>

          {isCustomOrderEnabled && (
            <>
              <button
                className={`btn btn-sm ${isDragMode ? "btn-warning text-dark" : "btn-primary"}`}
                onClick={() => setIsDragMode(!isDragMode)}
                style={{ fontSize: '11px', padding: '2px 8px', height: '26px', display: 'flex', alignItems: 'center', transition: 'all 0.2s' }}
                title="Modificar el orden de los casos"
              >
                {isDragMode ? (
                  <><i className="fas fa-save me-1"></i>Guardar Cambios</>
                ) : (
                  <><i className="fas fa-arrows-alt me-1"></i>Reordenar</>
                )}
              </button>

              <button
                className="btn btn-sm btn-outline-danger"
                onClick={() => {
                  localStorage.removeItem('casosCustomOrder');
                  setCustomOrder([]);
                  setIsCustomOrderEnabled(false);
                  setIsDragMode(false);
                  localStorage.setItem('casosCustomOrderEnabled', 'false');
                }}
                style={{ fontSize: '11px', padding: '2px 8px', height: '26px', display: 'flex', alignItems: 'center', transition: 'all 0.2s' }}
                title="Resetear al orden original"
              >
                <i className="fas fa-undo-alt me-1"></i>Resetear
              </button>
            </>
          )}
        </div>

        {isDragMode && (
          <div className="text-muted small">
            <i className="fas fa-info-circle me-1"></i>
            Arrastra las filas para reordenar los casos
          </div>
        )}
      </div>

      {/* Botones de acción - solo visible cuando hay casos seleccionados */}
      {selectedCasos.length > 0 && (
        <div className="mb-3 d-flex justify-content-between align-items-center">
          <div>
            <span className="text-muted">
              {selectedCasos.length} caso{selectedCasos.length > 1 ? 's' : ''} seleccionado{selectedCasos.length > 1 ? 's' : ''}
              {isSelectingAllPages && (
                <span className="text-primary ms-2">
                  <i className="fas fa-globe"></i> (de todas las páginas)
                </span>
              )}
            </span>
          </div>
          <div className="d-flex gap-2">
            <Button
              color="secondary"
              size="sm"
              onClick={() => {
                setSelectedCasos([]);
                setIsSelectingAllPages(false);
                setAllPagesCasosIds([]);
              }}
            >
              Limpiar Selección
            </Button>

            {/* Botón Asignar - solo para roles administrativos */}
            {canAssignCases() && (
              <Button
                color="primary"
                size="sm"
                onClick={handleAsignarCasos}
              >
                Asignar OT
              </Button>
            )}

            {/* Botón Transferir - solo para agentes */}
            {canTransferCases() && (
              <Button
                color="success"
                size="sm"
                onClick={handleTransferirCasos}
              >
                Transferir
              </Button>
            )}

            {/* Botón para generación masiva de DF - solo SuperAdmin */}
            {canGenerateDF() && (
              <Button
                color="warning"
                size="sm"
                onClick={() => setIsMassModalOpen(true)}
              >
                Generar DF
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Renderizado condicional: con drag and drop o tabla normal */}
      {isDragMode ? (
        <DragDropContext onDragEnd={handleOnDragEnd}>
          <div className="table-responsive">
            <table className={`table table-hover ${theme === 'dark' ? 'table-dark' : ''}`}>
              <thead>
                <tr>
                  <th style={{ width: '40px' }}>Orden</th>
                  {columns.map(column => (
                    <th key={column.key}>{column.label}</th>
                  ))}
                </tr>
              </thead>
              <Droppable droppableId="casos-list">
                {(provided) => (
                  <tbody {...provided.droppableProps} ref={provided.innerRef}>
                    {formattedData.map((item, index) => (
                      <Draggable
                        key={item.id}
                        draggableId={item.id.toString()}
                        index={index}
                      >
                        {(provided, snapshot) => (
                          <tr
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            style={{
                              ...provided.draggableProps.style,
                              backgroundColor: snapshot.isDragging ? '#f0f8ff' : 'transparent',
                            }}
                          >
                            <td style={{ width: '40px', textAlign: 'center', padding: '8px' }}>
                              <span style={{ cursor: 'grab', color: '#666' }}>⋮⋮</span>
                            </td>
                            {columns.map(column => {
                              if (column.key === 'select') {
                                return (
                                  <td key={column.key}>
                                    {item[column.key]}
                                  </td>
                                );
                              }
                              return (
                                <td key={column.key}>
                                  {typeof item[column.key] === 'object' && item[column.key] !== null
                                    ? item[column.key]
                                    : item[column.key] || 'N/A'
                                  }
                                </td>
                              );
                            })}
                          </tr>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </tbody>
                )}
              </Droppable>
            </table>
          </div>
        </DragDropContext>
      ) : (
        <Table
          data={formattedData}
          columns={columns}
          theme={theme}
          onRowClick={onRowClick}
          viewExport={false}
          viewImport={false}
        />
      )}

      {/* Modal de procesamiento masivo de informes */}
      {/* Modal de Correspondencia */}
      {selectedCorrespondencia && (
        <CorrespondenciaModal
          isOpen={showCorrespondenciaModal}
          toggle={() => setShowCorrespondenciaModal(!showCorrespondenciaModal)}
          caso={selectedCorrespondencia}
          onUpdate={() => safeRefresh()}
        />
      )}

      {/* Modal de Procesamiento Masivo */}
      <ProcesarInformesMasivos
        isOpen={isMassModalOpen}
        onClose={() => setIsMassModalOpen(false)}
        selectedIds={isSelectingAllPages && allPagesCasosIds.length > 0 ? allPagesCasosIds : selectedCasos}
      />
    </div>
  );
};

export default ListaCasos;
